import React, { useState, useRef, useEffect, useCallback } from "react";
import { callAPI } from "../helper/apiUtils";

// Returns the trailing comma-separated segment the user is currently
// editing, e.g. for "alice@x.com, bo" it returns "bo".
function getCurrentSegment(value) {
  const parts = String(value || "").split(",");
  return (parts[parts.length - 1] || "").trim();
}

// Replace just the trailing segment with the chosen email and append a
// ", " so the user can keep typing the next recipient immediately.
function replaceCurrentSegment(value, email) {
  const parts = String(value || "").split(",");
  parts[parts.length - 1] = ` ${email}`;
  return parts.join(",") + ", ";
}

/**
 * Plain text input with email-contacts autocomplete.
 *
 * Mirrors the existing comma-separated recipient input semantics so it
 * is a drop-in replacement for the raw <input> elements used by the
 * compose UIs. As the user types in the trailing segment we
 * debounce-fetch /email/contacts and render a dropdown with the top
 * matches; selecting one (mouse, Enter, or Tab) replaces just that
 * segment with the contact's email and appends ", " so the next
 * recipient can be typed immediately.
 */
function EmailRecipientInput({
  value,
  onChange,
  onKeyDown,
  disabled,
  placeholder,
  className,
  inputRef,
  ariaLabel,
  accountId,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const internalInputRef = useRef(null);
  const inputElementRef = inputRef || internalInputRef;
  const debounceRef = useRef(null);
  const requestSeqRef = useRef(0);

  const fetchContacts = useCallback(
    (q) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        const seq = ++requestSeqRef.current;
        try {
          const params = { q, limit: 8 };
          if (accountId) params.accountId = accountId;
          const { data } = await callAPI("/email/contacts", params, "GET");
          if (seq !== requestSeqRef.current) return;
          if (data?.status && Array.isArray(data.contacts) && data.contacts.length > 0) {
            setSuggestions(data.contacts);
            setShowSuggestions(true);
            setActiveIndex(-1);
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
          }
        } catch (e) {
          if (seq !== requestSeqRef.current) return;
          setSuggestions([]);
          setShowSuggestions(false);
        }
      }, 200);
    },
    [accountId]
  );

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    const seg = getCurrentSegment(v);
    if (seg.length >= 1) {
      fetchContacts(seg);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setActiveIndex(-1);
    }
  };

  const applySuggestion = (contact) => {
    if (!contact || !contact.email) return;
    const newVal = replaceCurrentSegment(value, contact.email);
    onChange(newVal);
    setSuggestions([]);
    setShowSuggestions(false);
    setActiveIndex(-1);
    setTimeout(() => {
      if (inputElementRef.current) inputElementRef.current.focus();
    }, 0);
  };

  const handleKey = (e) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, -1));
        return;
      }
      if ((e.key === "Enter" || e.key === "Tab") && activeIndex >= 0) {
        e.preventDefault();
        applySuggestion(suggestions[activeIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setShowSuggestions(false);
        setActiveIndex(-1);
        return;
      }
    }
    if (onKeyDown) onKeyDown(e);
  };

  const handleFocus = () => {
    const seg = getCurrentSegment(value);
    if (seg.length >= 1) fetchContacts(seg);
  };

  useEffect(() => {
    if (!showSuggestions) return undefined;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showSuggestions]);

  return (
    <div className="email-recipient-input-wrap" ref={containerRef}>
      <input
        ref={inputElementRef}
        type="text"
        className={className || "email-compose-input"}
        value={value || ""}
        onChange={handleChange}
        onKeyDown={handleKey}
        onFocus={handleFocus}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoComplete="off"
      />
      {showSuggestions && suggestions.length > 0 && (
        <div className="email-recipient-suggestions">
          {suggestions.map((c, i) => (
            <div
              key={c.email}
              className={`email-recipient-suggestion ${i === activeIndex ? "active" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                applySuggestion(c);
              }}
              onMouseEnter={() => setActiveIndex(i)}
            >
              {c.name ? (
                <>
                  <span className="email-recipient-suggestion-name">{c.name}</span>
                  <span className="email-recipient-suggestion-email">&lt;{c.email}&gt;</span>
                </>
              ) : (
                <span className="email-recipient-suggestion-email">{c.email}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default EmailRecipientInput;
